import { router } from "@inertiajs/react";
import { Box, Container, Heading, Button, Text, Link as CLink, VStack, HStack, Card } from "@chakra-ui/react";
import { Link } from "@inertiajs/react";
import { useAuth } from "@/hooks/useAuth";
import { my_settings_path, my_settings_accounts_path } from "@/routes";

interface Props {
  has_google: boolean;
  has_line: boolean;
}

type Provider = "google_oauth2" | "line";

export default function Accounts({ has_google, has_line }: Props) {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const handleDisconnect = (provider: Provider) => {
    const confirmMessage = "本当に連携を解除しますか？";
    if (confirm(confirmMessage)) {
      router.delete(my_settings_accounts_path(), {
        data: { provider },
      });
    }
  };

  const handleConnect = (provider: Provider) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `/auth/${provider}`;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
    if (csrfToken) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "authenticity_token";
      input.value = csrfToken;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <Container maxW="2xl" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <Box>
          <CLink as={Link} href={my_settings_path()} fontSize="sm">
            ← 設定に戻る
          </CLink>
          <Heading size="2xl" mt={4}>
            アカウント連携編集
          </Heading>
          <Text color="gray.600" mt={2}>
            外部アカウントとの連携を管理してください
          </Text>
        </Box>

        {/* Main Card */}
        <Card.Root bg="white" shadow="lg" borderRadius="xl">
          <Card.Body p={8}>
            <VStack gap={6} align="stretch">
              {/* Google Account Section */}
              <Box>
                <HStack justify="space-between" align="center" mb={4}>
                  <Box>
                    <HStack gap={3} align="center">
                      <Box fontSize="2xl">🔵</Box>
                      <Box>
                        <Text fontWeight="semibold" fontSize="lg">
                          Google アカウント
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {has_google ? "連携済み" : "未連携"}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                  <Box>
                    {has_google ? (
                      <Button
                        colorPalette="red"
                        size="lg"
                        onClick={() => handleDisconnect("google_oauth2")}
                        disabled={!has_line}
                        borderRadius="lg"
                        _hover={{
                          transform: !has_line ? "none" : "translateY(-1px)",
                          shadow: !has_line ? "none" : "md",
                        }}
                        _disabled={{
                          opacity: 0.6,
                          cursor: "not-allowed",
                        }}
                        transition="all 0.2s"
                      >
                        Google連携 解除
                      </Button>
                    ) : (
                      <Button
                        colorPalette="blue"
                        size="lg"
                        onClick={() => handleConnect("google_oauth2")}
                        borderRadius="lg"
                        _hover={{
                          transform: "translateY(-1px)",
                          shadow: "md",
                        }}
                        transition="all 0.2s"
                      >
                        Google連携
                      </Button>
                    )}
                  </Box>
                </HStack>
              </Box>

              {/* Divider */}
              <Box height="1px" bg="gray.200" />

              {/* LINE Account Section */}
              <Box>
                <HStack justify="space-between" align="center" mb={4}>
                  <Box>
                    <HStack gap={3} align="center">
                      <Box fontSize="2xl">🟢</Box>
                      <Box>
                        <Text fontWeight="semibold" fontSize="lg">
                          LINE アカウント
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {has_line ? "連携済み" : "未連携"}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                  <Box>
                    {has_line ? (
                      <Button
                        colorPalette="red"
                        size="lg"
                        onClick={() => handleDisconnect("line")}
                        disabled={!has_google}
                        borderRadius="lg"
                        _hover={{
                          transform: !has_google ? "none" : "translateY(-1px)",
                          shadow: !has_google ? "none" : "md",
                        }}
                        _disabled={{
                          opacity: 0.6,
                          cursor: "not-allowed",
                        }}
                        transition="all 0.2s"
                      >
                        LINE連携 解除
                      </Button>
                    ) : (
                      <Button
                        colorPalette="green"
                        size="lg"
                        onClick={() => handleConnect("line")}
                        borderRadius="lg"
                        _hover={{
                          transform: "translateY(-1px)",
                          shadow: "md",
                        }}
                        transition="all 0.2s"
                      >
                        LINE連携
                      </Button>
                    )}
                  </Box>
                </HStack>
              </Box>

              {/* Information Box */}
              <Box bg="gradient-to-r from-blue.50 to-purple.50" p={6} borderRadius="xl" border="1px solid" borderColor="blue.100">
                <Text fontSize="sm" color="gray.700" mb={2} fontWeight="semibold">
                  ℹ️ アカウント連携について
                </Text>
                <VStack gap={1} align="start">
                  <Text fontSize="xs" color="gray.600">
                    • 少なくとも1つのアカウント連携が必要です
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    • 連携解除する場合は、他のアカウントが連携されている必要があります
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    • 連携したアカウントでログインできます
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Container>
  );
}
